<!-- BaseModal.vue -->
<script setup>
import { defineProps, defineEmits, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['close']);

const close = () => {
  emit('close');
};

const onEscapeKey = (event) => {
  if (event.key === 'Escape') {
    close();
  }
};

onMounted(() => {
  document.addEventListener('keydown', onEscapeKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscapeKey);
});
</script>

<template>
  <div
    class="modal-overlay"
    v-if="show"
    @click.self="close"
    aria-modal="true"
    role="dialog"
  >
    <div class="modal-content">
      <!-- Close Button -->
      <button class="modal-close-button" @click="close" aria-label="Close Modal">
        &times;
      </button>

      <!-- Icon Slot -->
      <div class="modal-icon">
        <slot name="icon"></slot>
      </div>

      <!-- Message Slot -->
      <div class="modal-message">
        <slot name="message"></slot>
      </div>

      <!-- Actions Slot -->
      <div class="modal-actions">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Modal Content */
.modal-content {
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  text-align: center;
  position: relative;
  animation: fadeInUp 0.3s ease;
}

/* Close Button */
.modal-close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  color: #999999;
  transition: color 0.2s ease;
}

.modal-close-button:hover {
  color: #333333;
}

/* Modal Icon */
.modal-icon {
  margin-bottom: 1.5rem;
}

.modal-icon i {
  font-size: 4rem;
}

/* Modal Message */
.modal-message {
  margin-bottom: 2rem;
  font-size: 1.2rem;
  color: #555555;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal-actions .button {
  padding: 0.75rem;
  cursor: pointer;
  width: 100%;
  border-radius: 8px;
  border: none;
  font-size: 1rem;
  font-weight: bold;
}

.button-continue {
  background-color: #007bff;
  color: #ffffff;
  transition: background-color 0.2s ease;
}

.button-continue:hover {
  background-color: #0056b3;
}

.button-close {
  background-color: #f0f0f0;
  color: #333333;
  transition: background-color 0.2s ease;
}

.button-close:hover {
  background-color: #e0e0e0;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
